import { Text } from '@mantine/core';

import { languages as appSupportedLanguages } from '@repo/core/constants';

export const customRound = (num, places = 2, addingTrailingZeros = true) => {
    const shift = `${num}e+${places}`;
    const rounded = Math.round(Number(shift));
    let roundNum = Number(`${rounded}e-${places}`);

    if (places !== 0 && addingTrailingZeros) {
        roundNum = roundNum.toString();
        let intPart = roundNum;
        let decimalPart = '';

        // if we have a decimal number we split it into two parts
        if (roundNum.includes('.')) {
            roundNum = roundNum.split('.');
            intPart = roundNum[0];
            decimalPart = roundNum[1];
        }

        // if the size of the decimal part is not equal to the number of digits after the decimal point given in parameter, we add the missing zeros
        if (decimalPart.length !== places) {
            const numOfMissingZero = places - decimalPart.length;
            decimalPart = decimalPart.padEnd(numOfMissingZero + decimalPart.length, '0');
        }
        roundNum = `${intPart}.${decimalPart}`;
    }
    return roundNum;
};

export const getAccess = (links, rel) => {
    return links?.find((link) => link.rel === rel);
};

export const getAccessElements = (links, rel) => {
    const link = links?.find((link) => link.rel === rel);

    if (!link) {
        return { method: '', path: '' };
    }

    const method = link.method.toLowerCase();
    const path = link.href;
    return { method, path };
};

// Extraire le domaine parent dynamiquement
export const getDomain = (hostname) => {
    if (!hostname) return undefined;

    // Enlever le port si présent
    const cleanHost = hostname.split(':')[0];

    // Si localhost ou IP, pas de domaine parent
    if (cleanHost === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(cleanHost)) {
        return undefined;
    }

    // Extraire les deux dernières parties (domaine parent)
    const parts = cleanHost.split('.');
    if (parts.length >= 2) {
        return `.${parts.slice(-2).join('.')}`;
    }

    return undefined;
};

export const getPathCategory = (category, code, path, locale) => {
    if (category.code === code) {
        return [...path, category.slug[locale]];
    } else {
        for (let child of category.children) {
            let newPath = getPathCategory(child, code, [...path, category.slug[locale]], locale);
            if (newPath) {
                return newPath;
            }
        }
    }
    return null;
};

export const getUrlCategory = (category, item, locale) => {
    if (item.action === 'catalog' || item.action === 'categorylist') {
        let path = [];
        path = getPathCategory(category, item.code, path, locale);
        return `/c/${path.join('/')}`;
    } else if (item.action === 'page') {
        return `/${item.pageSlug}`;
    } else if (item.url) {
        return item.url;
    } else {
        return '/';
    }
};

// Get camel case props component
export const getCamelCaseProps = (originalesProps, props) => {
    let newProps = {};
    for (const key of originalesProps) {
        if (props[key.toLowerCase()]) {
            newProps[key] = props[key.toLowerCase()];
        }
    }
    return newProps;
};

// Vérfie si un id est un id Mongo valide
export const isValidObjectId = (id) => {
    return typeof id === 'string' && id.length === 24 && /^[a-fA-F0-9]{24}$/.test(id);
};

export const encodeObjectToBase64 = (obj) => {
    if (obj && typeof obj === 'object') obj = Buffer.from(JSON.stringify(obj)).toString('base64');
    return obj;
};

export const decodeBase64ToObject = (obj) => {
    if (obj && typeof obj !== 'object') obj = JSON.parse(Buffer.from(obj, 'base64').toString('utf8'));
    return obj;
};

export const formatDateTime = (date, t) => {
    if (!date) return '';

    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0'); // les mois commencent à 0
    const year = String(d.getFullYear());

    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return t('date-at-time', {
        date: `${day}/${month}/${year}`,
        time: `${hours}:${minutes}`,
    });
};

export const formatFirstnameLastname = (fullname) => {
    const splitFullname = fullname.split(' ');

    if (splitFullname.length > 1) {
        const firstname = splitFullname[1].charAt(0);
        const newFormat = `${firstname}. ${splitFullname[0]}`;
        return newFormat;
    }

    return fullname;
};

export const formatShortDateTime = (date, locale) => {
    if (date) {
        return new Date(date).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'medium' });
    }
    return '';
};

export const boondFindElementById = (data, id) => {
    for (let i = 0; i < data?.length; i++) {
        const element = data[i];

        // Vérifier si l'élément actuel a le même ID que celui recherché
        if (element.id === id) {
            return element.value; // Retourner la valeur de l'élément trouvé
        }

        // Si l'élément a une propriété "option" (sous-éléments), rechercher récursivement dedans
        if (element.option?.length) {
            const foundInOptions = boondFindElementById(element.option, id);
            if (foundInOptions) {
                return foundInOptions; // Retourner l'élément trouvé dans les options
            }
        }
    }

    return '';
};

export const flattenBoondOptions = (data) => {
    const values = [];
    for (let i = 0; i < data?.length; i++) {
        const element = data[i];
        if (element.option?.length) {
            values.push(...flattenBoondOptions(element.option));
        } else {
            values.push(element);
        }
    }
    return values;
};

export const removeObjectIds = (obj, isRoot) => {
    for (const key in obj._doc) {
        if (isRoot === false && key === '_id') {
            delete obj._doc._id;
        } else if ((Object.prototype.toString.call( obj[key] ) === '[object Array]' )) {
            for (let i = 0; i < obj[key].length; i++)
                removeIds(obj[key][i], false);
        }
    }
    return obj;
};

/**
 * @returns {JSX.Element}
 */
export const getLabelCredits = (credits, t, size = 'xs') => {
    if (credits.unlimited) {
        return <Text fz={size} c="gray">
            {t('unlimited-credits')}
        </Text>;
    }
    if (credits.value === 0) {
        return <Text fz={size} c="red">
            {t('credits-depleted')}
        </Text>;
    }
    return <Text fz={size} c={!credits.value ? 'red' : 'inherit'}>
        {credits.value > 1 ? t('remaining-credits', { count: credits.value }) : t('remaining-credit', { count: credits.value })}
    </Text>;
};

export function getJsonFromUrl() {
    const url = location.search;
    if (url === '') return {};
    const query = url.substr(1);
    const result = {};
    query.split('&').forEach(function(part) {
        const item = part.split('=');
        result[item[0]] = decodeURIComponent(item[1]);
    });
    return result;
}

export function queryParamsToString(obj) {
    return Object.keys(obj).filter((key) => key && obj[key] !== undefined).map((key) => `${key}=${obj[key]}`).join('&');
}

export function formatOppFormData(formDatas, opportunity) {
    const {
        description, description_context, description_tasks, description_place, description_homeOffice,
        criteria, criteria_authorizations, criteria_certifications, criteria_experience, criteria_hardSkills, criteria_languages, criteria_softSkills,
        thisAction, // champ technique du formulaire, ne doit pas être persisté dans la révision
        ...rest
    } = formDatas;
    return {
        ...rest,
        description: {
            context: description_context || '',
            tasks: description_tasks.map((task) => task.value) || [],
            homeOffice: description_homeOffice || '',
            place: description_place || '',
        },
        criteria: {
            authorizations: criteria_authorizations.map((auth) => auth.value) || [],
            certifications: criteria_certifications.map((certif) => certif.value) || [],
            experience: criteria_experience || '',
            hardSkills: criteria_hardSkills.map((hSkill) => hSkill.value) || [],
            languages: criteria_languages.map(({ language, level }) => ({ language, level })) || [],
            softSkills: criteria_softSkills || '',
        },
        client: { id: opportunity.data?.client?.id || opportunity.attributes?.client?.id || '' },
        contact: { id: opportunity.data?.contact?.id || opportunity.attributes?.contact?.id || '' },
    };
}

// select source slider (with date range) step generator function
export const generateStepsFromDefaultValue = () => {
    const steps = [];
    for (let i = 3; i <= 24; i = i + 3) {
        steps.push({ value: i });
    }
    return steps;
};

export const generateDateFilters = (t) => {
    const dateOptions = [];

    // add today filter
    dateOptions.push({ label: t('today'), value: new Date(Date.now()).setHours(0, 0, 0, 0).toString() });
    //add this week filter
    dateOptions.push({ label: t('this-week'), value: getMonday().setHours(0, 0, 0, 1).toString() });

    //add this month filter
    dateOptions.push({ label: t('this-month'), value: getFirstDateOfMonth().setHours(0, 0, 0, 2).toString() });

    // trimestre
    dateOptions.push({ label: t('this-quarter'), value: new Date(new Date(Date.now()).setHours(0, 0, 0, 0)).setDate(new Date().getDate() - (30 * 3)).toString() });

    //semestre
    dateOptions.push({ label: t('this-semester'), value: new Date(new Date(Date.now()).setHours(0, 0, 0, 0)).setDate(new Date().getDate() - (365 / 2)).toString() });

    //annee
    dateOptions.push({ label: t('this-year'), value: new Date(new Date(Date.now()).setHours(0, 0, 0, 0)).setDate(new Date().getDate() - 365).toString() });

    return dateOptions;
};

export const getMonday = (d = new Date()) => {
    const day = d.getDay(),
        diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    return new Date(d.setDate(diff));
};

export const getFirstDateOfMonth = (date = new Date()) =>
    new Date(date.getFullYear(), date.getMonth(), 1);

export const getLabelTypeOpportunity = (aType, t) => {
    switch (aType) {
        case 'opportunity':
            return t('imported');
        case 'ai':
            return t('ai');
        default:
            return t('modified');
    }
};
export const getLabelTypeDt = (aType, t) => {
    switch (true) {
        case aType === 'dt':
            return t('dt-import');
        case aType === 'cv':
            return t('cv-import');
        case aType === 'bkp':
            return t('backup');
        case aType === 'ai':
        case aType === 'ia':
            return t('ai');
        case aType === 'dt-neylia':
            return t('dt-neylia');
        case aType === 'mini-cv':
            return t('mini-cv');
        case /:modified/gm.test(aType):
            return t('modified');
        default:
            return '-';
    }
};

const ISO_639_1_LANGUAGE_CODE_REGEX = /^[a-z]{2}$/;
const DEFAULT_DOCUMENT_LANGUAGE_CODE = 'fr';

const normalizeIso6391LanguageCode = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
        return '';
    }

    if (ISO_639_1_LANGUAGE_CODE_REGEX.test(normalizedValue)) {
        return normalizedValue;
    }

    const localePrefix = normalizedValue.split(/[-_]/)[0];
    if (ISO_639_1_LANGUAGE_CODE_REGEX.test(localePrefix)) {
        return localePrefix;
    }

    return '';
};

const resolveSupportedTranslationLanguageCodes = (supportedLanguageCodes, displayLanguage) => {
    let rawLanguageCodes = [];

    if (Array.isArray(supportedLanguageCodes) && supportedLanguageCodes.length) {
        rawLanguageCodes = supportedLanguageCodes;
    } else if (Array.isArray(appSupportedLanguages) && appSupportedLanguages.length) {
        rawLanguageCodes = appSupportedLanguages
            .map((language) => language?.value)
            .filter((languageCode) => typeof languageCode === 'string');
    } else if (typeof Intl.supportedValuesOf === 'function') {
        try {
            rawLanguageCodes = Intl.supportedValuesOf('language');
        } catch {
            rawLanguageCodes = [];
        }
    }

    const normalizedCodes = [];
    const languageCodesMap = new Set();

    rawLanguageCodes.forEach((languageCode) => {
        const normalizedLanguageCode = normalizeIso6391LanguageCode(languageCode);
        if (normalizedLanguageCode && !languageCodesMap.has(normalizedLanguageCode)) {
            languageCodesMap.add(normalizedLanguageCode);
            normalizedCodes.push(normalizedLanguageCode);
        }
    });

    if (!normalizedCodes.length) {
        const normalizedDisplayLanguage = normalizeIso6391LanguageCode(displayLanguage);
        if (normalizedDisplayLanguage) {
            normalizedCodes.push(normalizedDisplayLanguage);
        }
    }

    return normalizedCodes;
};

export const getDocumentLanguageCode = (language, fallback = DEFAULT_DOCUMENT_LANGUAGE_CODE.toUpperCase()) => {
    const normalizedLanguage = normalizeIso6391LanguageCode(language);
    if (normalizedLanguage) {
        return normalizedLanguage.toUpperCase();
    }

    const normalizedFallback = normalizeIso6391LanguageCode(fallback);
    if (normalizedFallback) {
        return normalizedFallback.toUpperCase();
    }

    return DEFAULT_DOCUMENT_LANGUAGE_CODE.toUpperCase();
};

export const getLocalizedLanguageLabel = (languageCode, displayLanguage = '') => {
    const normalizedLanguageCode = normalizeIso6391LanguageCode(languageCode);
    if (!normalizedLanguageCode) {
        return '';
    }

    const normalizedDisplayLanguage = normalizeIso6391LanguageCode(displayLanguage);

    let localizedLabel = '';
    try {
        const displayNames = new Intl.DisplayNames(
            normalizedDisplayLanguage ? [normalizedDisplayLanguage] : undefined,
            { type: 'language' },
        );
        localizedLabel = displayNames.of(normalizedLanguageCode) || '';
    } catch {
        localizedLabel = '';
    }

    if (!localizedLabel) {
        localizedLabel = normalizedLanguageCode.toUpperCase();
    }

    return localizedLabel.charAt(0).toUpperCase() + localizedLabel.slice(1);
};

export const getTranslationLanguageOptions = (displayLanguage = '', supportedLanguageCodes = null) => {
    const normalizedDisplayLanguage = normalizeIso6391LanguageCode(displayLanguage);
    const translationLanguageCodes = resolveSupportedTranslationLanguageCodes(
        supportedLanguageCodes,
        normalizedDisplayLanguage,
    );

    return translationLanguageCodes
        .map((languageCode) => {
            const label = getLocalizedLanguageLabel(languageCode, normalizedDisplayLanguage);
            return {
                value: languageCode,
                label,
                code: languageCode,
            };
        })
        .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label, normalizedDisplayLanguage || undefined));
};

export const disableTranslationLanguageOption = (options = [], disabledLanguageCode) => {
    const normalizedDisabledLanguageCode = normalizeIso6391LanguageCode(disabledLanguageCode);

    return options.map((option) => ({
        ...option,
        disabled:
            !!normalizedDisabledLanguageCode &&
            normalizeIso6391LanguageCode(option.code) === normalizedDisabledLanguageCode,
    }));
};

export const getDefaultTranslationLanguageCode = (options = [], sourceLanguageCode, preferredLanguageCode) => {
    const normalizedSourceLanguageCode = normalizeIso6391LanguageCode(sourceLanguageCode);
    const normalizedPreferredLanguageCode = normalizeIso6391LanguageCode(preferredLanguageCode);

    const enabledOptions = options.filter((option) => !option.disabled && normalizeIso6391LanguageCode(option.code));
    if (!enabledOptions.length) {
        return '';
    }

    if (normalizedPreferredLanguageCode) {
        const preferredOption = enabledOptions.find(
            (option) => normalizeIso6391LanguageCode(option.code) === normalizedPreferredLanguageCode,
        );
        if (preferredOption) {
            return preferredOption.value;
        }
    }

    const firstDifferentSourceOption = enabledOptions.find(
        (option) =>
            !normalizedSourceLanguageCode || normalizeIso6391LanguageCode(option.code) !== normalizedSourceLanguageCode,
    );
    if (firstDifferentSourceOption) {
        return firstDifferentSourceOption.value;
    }

    return enabledOptions[0].value;
};

export const getSelectedTranslationLanguageOption = (options = [], selectedLanguageCode = '') => {
    if (!selectedLanguageCode) {
        return { status: 'missing', option: null };
    }

    const selectedOption = options.find((option) => option.value === selectedLanguageCode);
    if (!selectedOption) {
        return { status: 'invalid', option: null };
    }

    if (selectedOption.disabled) {
        return { status: 'disabled', option: selectedOption };
    }

    return { status: 'ok', option: selectedOption };
};

export const boondURLs = {
    candidate: (candidateId) => `https://ui.boondmanager.com/candidates/${candidateId}/overview`,
    opportunity: (opportunityId) => `https://ui.boondmanager.com/opportunities/${opportunityId}/information`,
    contact: (contactId) => `https://ui.boondmanager.com/contacts/${contactId}/overview`,
    company: (companyId) => `https://ui.boondmanager.com/companies/${companyId}/overview`,
    resource: (resourceId) => `https://ui.boondmanager.com/resources/${resourceId}/overview`,
};

export const getMonthByNumber = (monthNumber, t) => {
    if (!monthNumber) return '';
    const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    const index = parseInt(monthNumber, 10) - 1;
    if (index >= 0 && index < 12) {
        return t(months[index]);
    }
    return monthNumber;
};

export const camelToKebab = (str) => {
    if (typeof str !== 'string') return str;
    return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
};

export const getDictValue = (dictKey, id, dictionaryIds, language = 'fr') => {
    if (!dictionaryIds || !dictionaryIds[dictKey]) return id;

    // Handle arrays if multiple IDs are passed
    if (Array.isArray(id)) {
        return id.map((i) => getDictValue(dictKey, i, dictionaryIds, language)).join(', ');
    }

    // Ensure id is a string/number, extract if it's an object
    const targetId = (typeof id === 'object' && id !== null) ? (id.id || id.value) : id;

    // Recursive function to search in the tree (nested "options")
    const findInTree = (items, target) => {
        for (const item of items) {
            // Check current level
            if (String(item.id) === String(target)) {
                return item;
            }
            // Check children if they exist
            if (item.option && Array.isArray(item.option)) {
                const foundNested = findInTree(item.option, target);
                if (foundNested) return foundNested;
            }
        }
        return null;
    };

    const found = findInTree(dictionaryIds[dictKey], targetId);

    if (!found) return id;

    if (found.value && typeof found.value === 'object') {
        return found.value[language] || Object.values(found.value)[0] || id;
    }

    return found.value || id;
};
